"use client";

import { isEmail } from "class-validator";
import { FC, useCallback, useState } from "react";
import { useRouter, useSearchParams } from "next/navigation";
import { AxiosError, HttpStatusCode } from "axios";
import { useAuth, useDatadog } from "@auth/client-sdk-react";
import { appendQueryparams } from "apps/website/utils/misc/merge-query";

import { DisplayState } from "@/constants/state";

import Button from "../../base/Button/Button";
import Spacer from "../../layout/Spacer/Spacer";
import { AlertPrompt } from "../AlertPrompt/AlertPrompt";
import Column from "../../layout/Column/Column";
import Input from "../../form/Input/Input";
import TextBody from "../../base/Text/TextBody/TextBody";
import { PASSCODE_LIFETIME_IN_MINS } from "libs/constants/auth/src/types";

interface IPasscodeLoginProps {
  email: string;
  disabled?: boolean;
  setDisabled(): void;
  passcodeSent: boolean;
  setPasscodeSent: (sent: boolean) => void;
  codeHasExpired: boolean;
  startPasscodeTimer: () => void;
}

const SomethingWentWrongAlert = () => (
  <AlertPrompt
    style="error"
    title="OOPS!" hideImage={false} >
    <TextBody size={3} align="center">
      Sorry, something went wrong. Please try again later.
    </TextBody>
    <Spacer size="md" />
  </AlertPrompt>
);

const LoginCodeInvalidatedAlert = () => (
  <AlertPrompt
    style="error"
    title="Invalid code" hideImage={false} >
    <TextBody size={3} align="center">
      Check if the code is entered correctly, or send another code to try again.
    </TextBody>
  </AlertPrompt>
);

const SuccessMessage = () => (
  <AlertPrompt
    style="success"
    title="Login code sent" hideImage={false} >
    <TextBody size={3} align="center">
      Enter your login code below. It can take up to 5 minutes to arrive, please check your spam folder too.
      Your code is valid for { PASSCODE_LIFETIME_IN_MINS } minutes.
    </TextBody>
  </AlertPrompt>
);

const SendAnotherCodeButton = ({ codeHasExpired, onEmailSubmit }
: { codeHasExpired: boolean, onEmailSubmit: () => void }) => (
  <Column direction="row" className="w-full" align="center">
    <div className="flex-1 h-0 -ml-4 md:ml-0" />
    <button
      disabled={!codeHasExpired}
      style={{ color: codeHasExpired ? "black" : "#6b7280" }}
      className="font-display underline text-sm mx-3"
      onClick={onEmailSubmit}>
      Send another code
    </button>
    <div className="flex-1 h-0 -mr-4 md:mr-0" />
  </Column>
);

const SubmitCodeSection = ({ loginCode, onPasscodeSubmit, isVerifying, onInputChange }
: {
  loginCode: string,
  onPasscodeSubmit: () => void,
  isVerifying: boolean,
  onInputChange: (value: string) => void
}) => (
  <>
    <Input
      value={loginCode}
      onChange={(event) => onInputChange(event.target.value)}
      className="md:w-96 w-full"
      name="Passcode"
      label="Login code"
      placeholder="Input login code here"
      optional={true}
    />
    <Spacer size="md" />
    <Button
      disabled={loginCode.length !== 6}
      onClick={onPasscodeSubmit}
      state={isVerifying ? DisplayState.PROCESSING : DisplayState.READY}
    >
      login
    </Button>
  </>
);

export const PasscodeLogin: FC<IPasscodeLoginProps> = ({
  email,
  disabled,
  setDisabled,
  passcodeSent,
  setPasscodeSent,
  codeHasExpired,
  startPasscodeTimer,
}) => {

  const datadog = useDatadog();
  const auth = useAuth();
  const router = useRouter();
  const searchParams = useSearchParams();
  const validEmail = isEmail(email);

  const [ loginCode, setLoginCode ] = useState("");
  const [ isSending, setIsSending ] = useState(false);
  const [ isOtherError, setIsOtherError ] = useState(false);
  const [ isInvalidCodeError, setIsInvalidCodeError ] = useState(false);
  const [ isVerifying, setIsVerifying ] = useState(false);

  const handleCodeInputChange = useCallback((value: string) => {
    setLoginCode(value);

    if (isInvalidCodeError) {
      setIsInvalidCodeError(false);
    }
  }, [ isInvalidCodeError ]);

  const onEmailSubmit = useCallback(async () => {

    startPasscodeTimer();
    setDisabled();

    const postLogin = searchParams.get("postLogin") as string | undefined;

    try {
      await auth.sendPasscode(email, postLogin);
      setPasscodeSent(true);
      setIsInvalidCodeError(false);
    } catch (err) {
      datadog.logger.error("Error sending passcode", { err });
      setIsOtherError(true);
      setPasscodeSent(false);
      setIsSending(false);
    }
  }, [ email, auth, setDisabled, setPasscodeSent, startPasscodeTimer ]);

  const onPasscodeSubmit = useCallback(async () => {
    setIsVerifying(true);
    setIsSending(false);
    setIsInvalidCodeError(false);
    setIsOtherError(false);

    try {
      const result = await auth.verifyPasscode({ email, passcode: loginCode });
      if (result && "access_token" in result) {
        await auth.setLoginTokens(result, true);
        const { redirect = "/customer" } = result;
        const fullPath = appendQueryparams(redirect, searchParams);
        router.push(fullPath);
      }
    } catch (err) {

      datadog.logger.error("Error verifying passcode", { err });

      if (err instanceof AxiosError &&
        (err.response?.status === HttpStatusCode.BadRequest || err.response?.status === HttpStatusCode.NotFound)) {
        setIsInvalidCodeError(true);
      } else {
        setIsOtherError(true);
      }
      setIsVerifying(false);
    }
  }, [ email, loginCode, auth, router ]);

  return (
    <Column component={PasscodeLogin.name} align="center">
      { passcodeSent && (
        <>
          <SuccessMessage/>
          <Spacer size="lg" />
          <SendAnotherCodeButton
            codeHasExpired={codeHasExpired}
            onEmailSubmit={onEmailSubmit}
          />
          <Spacer size="2xl" />
          <SubmitCodeSection
            isVerifying={isVerifying}
            loginCode={loginCode}
            onPasscodeSubmit={onPasscodeSubmit}
            onInputChange={handleCodeInputChange}
          />
        </>
      ) }
      { isInvalidCodeError && (
        <>
          <Spacer size="md" />
          <LoginCodeInvalidatedAlert/>
        </>
      ) }
      { isOtherError && (
        <>
          <Spacer size="md" />
          <SomethingWentWrongAlert/>
        </>
      ) }
      { !passcodeSent && (
        <Button
          onClick={onEmailSubmit}
          disabled={isSending || disabled || !validEmail}
          state={isSending ? DisplayState.PROCESSING : DisplayState.READY}
        >
          SEND ME A LOGIN CODE
        </Button>
      ) }
    </Column>
  );
};
